import { useCallback, useEffect, useRef } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import apiClient from "@/lib/api-client";
import { getAuthToken } from "@/lib/auth";
import { streamSSE } from "@/lib/sse-client";
import { useZoteroBulkImportStore } from "@/stores/zotero-bulk-import-store";
import type { BulkImportRole, BulkImportSnapshot } from "@/types/zoteroBulkImport";

interface StartOptions {
  collection_key: string | null;
  concept_id: string;
  role: BulkImportRole;
  // Plan 1.5 allow-list mode — import only these attachment keys, fetched
  // across collection_keys. Omitted (undefined) => legacy whole-collection.
  attachment_keys?: string[];
  collection_keys?: string[];
}

interface StartResponse {
  job_id: string;
}

// How long to wait after cancel POST before reconciling via /active.
// 15s is enough for an in-flight Zotero item (HTTP fetch + R2 upload) to
// finish and the backend to push its terminal event. Tests override via
// `setCancelReconcileMs` to keep timing-dependent assertions fast.
let CANCEL_RECONCILE_MS = 15_000;

/** Test-only: override the reconciliation timeout. */
export function setCancelReconcileMs(ms: number): void {
  CANCEL_RECONCILE_MS = ms;
}

/**
 * Consume the bulk-import SSE stream. Pure function — no React state,
 * just dispatches each parsed event to the Zustand store + handles terminal
 * events. Returned promise resolves on terminal event or stream close.
 */
async function consumeBulkImportStream(
  jobId: string,
  signal: AbortSignal,
  onComplete: (snapshot: BulkImportSnapshot) => void,
  onError: (snapshot: BulkImportSnapshot) => void,
): Promise<void> {
  const baseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8000";
  const token = await getAuthToken();
  const url = `${baseUrl}/api/zotero-sync/import-collection/${jobId}/stream`;
  const store = useZoteroBulkImportStore;

  let receivedTerminal = false;
  try {
    for await (const event of streamSSE(
      url,
      { Authorization: `Bearer ${token}` },
      signal,
    )) {
      const data = event.data ? JSON.parse(event.data) : {};

      if (event.event === "step:fetch_collection") {
        if (typeof data.fetched === "number") {
          const s = store.getState();
          const currentStatus: BulkImportSnapshot["status"] =
            s.status === "idle" ? "running" : s.status;
          store.getState().mergeProgress({
            job_id: s.jobId ?? jobId,
            concept_id: s.conceptId ?? "",
            collection_key: s.collectionKey,
            role: s.role,
            status: currentStatus,
            error_reason: s.errorReason,
            reused_events: s.reusedEvents,
            fetched: data.fetched,
            added: s.added,
            duplicates: s.duplicates,
            failed: s.failed,
          });
        }
      } else if (event.event === "progress") {
        store.getState().mergeProgress(data as BulkImportSnapshot);
      } else if (event.event === "done") {
        receivedTerminal = true;
        onComplete(data as BulkImportSnapshot);
        return;
      } else if (event.event === "error") {
        receivedTerminal = true;
        onError(data as BulkImportSnapshot);
        return;
      }
    }
  } catch (err) {
    if ((err as { name?: string })?.name === "AbortError") {
      // Caller aborted — store state managed elsewhere.
      return;
    }
    const msg = err instanceof Error ? err.message : "SSE stream failed";
    toast.error("Bulk import stream error", { description: msg });
    return;
  }

  if (!receivedTerminal) {
    // Stream closed without a terminal event (server restart, network blip,
    // tab suspended). Stream closure is NOT evidence the import finished —
    // synthesizing a "done" snapshot here misleads the user, fires the
    // success toast, and invalidates caches as though the work completed.
    //
    // If the store already moved to a terminal status, preserve it
    // (shouldn't normally happen but defensive). Otherwise mark the job
    // as errored with a `stream_disconnected` reason and surface a toast.
    // Resume-on-mount will reconcile via /active on next page load if the
    // backend job is in fact still running.
    const s = store.getState();
    if (s.status === "cancelled" || s.status === "done") {
      // Already terminal locally — nothing to do.
      return;
    }
    onError({
      job_id: jobId,
      concept_id: s.conceptId ?? "",
      collection_key: s.collectionKey,
      role: s.role,
      status: "error",
      error_reason: "stream_disconnected",
      fetched: s.fetched,
      added: s.added,
      reused_events: s.reusedEvents,
      duplicates: s.duplicates,
      failed: s.failed,
    });
  }
}

/**
 * Apply a server-derived terminal snapshot to the store. Used by both the
 * SSE terminal-event path AND the cancel reconciliation path so they share
 * one source of truth for "what does the server say happened?" — and so
 * `done`/`cancelled`/`error` each fire the correct toast + cache
 * invalidations. Never call `complete("cancelled")` without verifying with
 * the server first; absence-of-active-job ≠ cancellation.
 */
export async function applyTerminalSnapshot(
  snapshot: BulkImportSnapshot,
  queryClient: QueryClient,
): Promise<void> {
  if (snapshot.status === "done") {
    // Timing-critical: curate.tsx's auto-score effect keys on the store's
    // status flipping to "done" and reads the ["papers"] cache synchronously
    // on that edge. So the imported papers MUST be in cache BEFORE we flip
    // any store field to "done". mergeProgress() sets status from the
    // snapshot ("done"), so we await the papers refetch first, then merge +
    // complete. Mirrors useSmartTrawl: await refetch, THEN signal completion.
    queryClient.invalidateQueries({ queryKey: ["zotero-sync-events"] });
    try {
      await queryClient.invalidateQueries({ queryKey: ["papers"] });
      await queryClient.invalidateQueries({ queryKey: ["paper-keys"] });
    } catch {
      // Refetch failed — proceed to terminal state anyway so the import
      // doesn't appear stuck (no success toast, OperationBar pill stuck
      // running). Auto-score may then run on a slightly stale cache, which is
      // preferable to a hang. void-callers also won't see an unhandled reject.
    }
    useZoteroBulkImportStore.getState().mergeProgress(snapshot);
    useZoteroBulkImportStore.getState().complete("done");
    toast.success("Bulk import complete", {
      description: `Added ${snapshot.added} paper(s)`,
    });
    return;
  }
  useZoteroBulkImportStore.getState().mergeProgress(snapshot);
  if (snapshot.status === "cancelled") {
    useZoteroBulkImportStore.getState().complete("cancelled");
    queryClient.invalidateQueries({ queryKey: ["zotero-sync-events"] });
    queryClient.invalidateQueries({ queryKey: ["papers"] });
    queryClient.invalidateQueries({ queryKey: ["paper-keys"] });
    toast("Bulk import cancelled", {
      description:
        `Added ${snapshot.added} paper(s) before cancel · ${snapshot.failed} failed`,
    });
  } else if (snapshot.status === "error") {
    useZoteroBulkImportStore
      .getState()
      .complete("error", snapshot.error_reason);
    queryClient.invalidateQueries({ queryKey: ["zotero-sync-events"] });
    toast.error("Bulk import failed", {
      description: snapshot.error_reason ?? "Unknown error",
    });
  } else {
    // Still pending/running — backend hasn't finished processing yet.
    useZoteroBulkImportStore.getState().hydrateFromSnapshot(snapshot);
  }
}

/**
 * Query the server for the actual terminal status of a job and apply it.
 * Used by the cancel-reconciliation paths (15s timeout + 409 race) to
 * avoid inferring cancellation from `/active` returning null — which
 * incorrectly maps `done`/`error` jobs into local `cancelled`.
 */
async function reconcileTerminalStatus(
  jobId: string,
  queryClient: QueryClient,
): Promise<void> {
  try {
    const snap = await apiClient
      .get(`api/zotero-sync/import-collection/${jobId}/status`)
      .json<BulkImportSnapshot | null>();
    if (!snap) {
      // Job evicted from the registry (>1h after completion) — can't
      // determine what actually happened.
      useZoteroBulkImportStore
        .getState()
        .complete("error", "status_unknown");
      toast.error("Bulk import status unknown", {
        description: "Refresh to check final state.",
      });
      return;
    }
    await applyTerminalSnapshot(snap, queryClient);
  } catch {
    useZoteroBulkImportStore
      .getState()
      .complete("error", "cancel_reconciliation_failed");
    toast.error("Cancel status unknown", {
      description: "Refresh the page to check import status.",
    });
  }
}

/**
 * Cancel the active bulk import. Module-level so all entry points
 * (modal, OperationBar pill) route through one failure-aware
 * implementation:
 *   - Cancel POST 202s → wait for SSE terminal; safety-net timeout
 *     reconciles via `/status/{jobId}` if the terminal never arrives.
 *   - Cancel POST 409s (job already terminal) → reconcile immediately.
 *   - Cancel POST otherwise fails → toast.error; leave store intact so
 *     resume-on-mount can recover.
 */
export async function cancelBulkImport(queryClient: QueryClient): Promise<void> {
  const { jobId } = useZoteroBulkImportStore.getState();
  if (!jobId) return;

  try {
    await apiClient.post(
      `api/zotero-sync/import-collection/${jobId}/cancel`,
    );
  } catch (err) {
    const errStatus = (err as { response?: { status?: number } })
      ?.response?.status;
    if (errStatus === 409) {
      await reconcileTerminalStatus(jobId, queryClient);
      return;
    }
    const msg = err instanceof Error ? err.message : "Cancel failed";
    toast.error("Cancel failed", {
      description: `${msg}. Refresh to check status.`,
    });
    return;
  }

  // Cancel POST accepted. Schedule reconciliation as a safety net.
  setTimeout(async () => {
    const s = useZoteroBulkImportStore.getState();
    if (s.jobId !== jobId) return;
    if (s.status !== "running" && s.status !== "pending") return;
    await reconcileTerminalStatus(jobId, queryClient);
  }, CANCEL_RECONCILE_MS);
}

export function useZoteroBulkImport() {
  const queryClient = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);

  const handleComplete = useCallback(
    (snapshot: BulkImportSnapshot) => {
      // Route through the shared terminal-snapshot applier so the SSE
      // terminal path uses the same logic as cancel reconciliation.
      void applyTerminalSnapshot(snapshot, queryClient);
    },
    [queryClient],
  );

  const handleError = useCallback(
    (snapshot: BulkImportSnapshot) => {
      void applyTerminalSnapshot(
        { ...snapshot, status: "error" },
        queryClient,
      );
    },
    [queryClient],
  );

  const start = useCallback(
    async (opts: StartOptions): Promise<boolean> => {
      // Cancel any in-flight SSE stream first.
      abortRef.current?.abort();

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await apiClient
          .post("api/zotero-sync/import-collection", {
            json: opts,
            timeout: 30_000,
          })
          .json<StartResponse>();

        // User cancelled while POST was in flight → discard the result.
        if (controller.signal.aborted) return true;

        useZoteroBulkImportStore.getState().setJobId(res.job_id);

        await consumeBulkImportStream(
          res.job_id,
          controller.signal,
          handleComplete,
          handleError,
        );
        return true;
      } catch (err) {
        if (controller.signal.aborted) return false;
        if ((err as { name?: string })?.name === "AbortError") return false;
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 409) {
          toast.error("A bulk import is already in progress for this concept.");
          return false;
        }
        const msg = err instanceof Error ? err.message : "Failed to start bulk import";
        toast.error("Bulk import failed", { description: msg });
        return false;
      }
    },
    [handleComplete, handleError],
  );

  const cancel = useCallback(
    async () => cancelBulkImport(queryClient),
    [queryClient],
  );

  // Resume an active job on mount / route remount. If a job is already
  // running (e.g. started from another tab/page), reattach the SSE stream
  // so progress reappears.
  useEffect(() => {
    const controller = new AbortController();
    let attached = false;

    (async () => {
      try {
        const active = await apiClient
          .get("api/zotero-sync/import-collection/active", {
            timeout: 10_000,
          })
          .json<BulkImportSnapshot | null>();

        if (!active || controller.signal.aborted) return;

        // Only reattach if we don't already have a running stream for this job.
        const store = useZoteroBulkImportStore.getState();
        if (store.jobId === active.job_id) return;

        attached = true;
        useZoteroBulkImportStore.getState().hydrateFromSnapshot(active);

        abortRef.current?.abort();
        abortRef.current = controller;

        await consumeBulkImportStream(
          active.job_id,
          controller.signal,
          handleComplete,
          handleError,
        );
      } catch {
        // 404 / network — no active job to resume. Silent fail.
      }
    })();

    return () => {
      if (!attached) controller.abort();
    };
  }, [handleComplete, handleError]);

  const state = useZoteroBulkImportStore();

  return { start, cancel, state };
}
